'use client';
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Check, X, Users } from 'lucide-react';
import { type FamilyMember } from '@/lib/claimStore';
import { fetchFamilyMembers, insertFamilyMember, updateFamilyMember, deleteFamilyMember } from '@/lib/claimService';
import { useClaimContext } from '@/contexts/ClaimContext';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { toast } from 'sonner';

const MEMBER_COLORS = [
  '#b45309', '#0369a1', '#7c3aed', '#0891b2',
  '#be185d', '#16a34a', '#d97706', '#dc2626',
];

export default function FamilyMembersSection() {
  const { activeClaim } = useClaimContext();
  const claimId = activeClaim?.id;

  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(MEMBER_COLORS[0]);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const data = await fetchFamilyMembers(claimId);
      setMembers(data);
      setLoading(false);
    }
    load();
  }, [claimId]);

  const startEdit = (member: FamilyMember) => {
    setEditingId(member.id);
    setEditName(member.name);
    setEditColor(member.color);
  };

  const saveEdit = async () => {
    if (!editName.trim() || !editingId) return;
    const ok = await updateFamilyMember(editingId, { name: editName.trim(), color: editColor });
    if (ok) {
      setMembers((prev) =>
        prev.map((m) => m.id === editingId ? { ...m, name: editName.trim(), color: editColor } : m)
      );
      toast.success('Member updated');
    } else {
      toast.error('Failed to update member');
    }
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    const saved = await insertFamilyMember({ name: newName.trim(), color: newColor }, claimId);
    if (saved) {
      setMembers((prev) => [...prev, saved]);
      setNewName('');
      setNewColor(MEMBER_COLORS[0]);
      setAddingNew(false);
      toast.success(`${saved.name} added as a family member`);
    } else {
      toast.error('Failed to add member');
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await deleteFamilyMember(id);
    if (ok) {
      setMembers((prev) => prev.filter((m) => m.id !== id));
      toast.success('Family member removed');
    } else {
      toast.error('Failed to remove member');
    }
    setDeleteTarget(null);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted-foreground text-sm">
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        Loading family members…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-primary" />
          <h2 className="text-base font-semibold text-foreground">Family Members</h2>
          {activeClaim && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
              {activeClaim.name}
            </span>
          )}
        </div>
        <button
          onClick={() => setAddingNew(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-amber-700 transition-all duration-150 active:scale-95"
        >
          <Plus size={13} /> Add Member
        </button>
      </div>
      <p className="text-sm text-muted-foreground">
        Track which family member documents each item for <strong>{activeClaim?.name ?? 'this claim'}</strong>.
      </p>

      <div className="space-y-2">
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center gap-3 p-3 bg-secondary/30 border border-border rounded-lg group"
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
              style={{ backgroundColor: member.color }}
            >
              {member.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
            </div>

            {editingId === member.id ? (
              <div className="flex-1 flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 min-w-[140px] px-2.5 py-1.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                />
                <div className="flex gap-1">
                  {MEMBER_COLORS.map((color) => (
                    <button
                      key={`color-edit-${color}`}
                      type="button"
                      onClick={() => setEditColor(color)}
                      className={`w-5 h-5 rounded-full border-2 transition-all ${editColor === color ? 'border-foreground scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="flex gap-1">
                  <button onClick={saveEdit} className="p-1.5 rounded hover:bg-positive-bg text-positive transition-colors">
                    <Check size={14} />
                  </button>
                  <button onClick={cancelEdit} className="p-1.5 rounded hover:bg-secondary text-muted-foreground transition-colors">
                    <X size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span className="flex-1 text-sm font-medium text-foreground">{member.name}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => startEdit(member)}
                    className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all duration-150 active:scale-95"
                    title="Edit member name"
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(member.id)}
                    className="p-1.5 rounded hover:bg-negative-bg text-muted-foreground hover:text-negative transition-all duration-150 active:scale-95"
                    title="Remove family member"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}

        {/* Add new member inline */}
        {addingNew && (
          <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg fade-in">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
              style={{ backgroundColor: newColor }}
            >
              {newName ? newName.split(' ').map((n) => n[0]).join('').slice(0, 2) : '?'}
            </div>
            <div className="flex-1 flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Full name"
                className="flex-1 min-w-[140px] px-2.5 py-1.5 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAddingNew(false); setNewName(''); } }}
              />
              <div className="flex gap-1">
                {MEMBER_COLORS.map((color) => (
                  <button
                    key={`color-new-${color}`}
                    type="button"
                    onClick={() => setNewColor(color)}
                    className={`w-5 h-5 rounded-full border-2 transition-all ${newColor === color ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={handleAdd}
                  disabled={!newName.trim()}
                  className="p-1.5 rounded hover:bg-positive-bg text-positive disabled:opacity-40 transition-colors"
                >
                  <Check size={14} />
                </button>
                <button
                  onClick={() => { setAddingNew(false); setNewName(''); }}
                  className="p-1.5 rounded hover:bg-secondary text-muted-foreground transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Remove family member?"
        description="This member will be removed from the list. Items they added will remain in the inventory but will show their name as the contributor."
        confirmLabel="Remove Member"
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}